"use client"

import { useEffect } from "react"

// Reference-counted, module-level scroll lock.
//
// Locks page scrolling by setting `overflow: hidden` on <html>/<body> while at
// least one overlay (sheet/dialog) is open. Crucially it does NOT use the
// `position: fixed` + scroll-restore technique: that momentarily resets the
// scroll to the top on release, which iOS Safari paints as a visible "jump to
// top then scroll back" flash. `overflow: hidden` leaves the existing scroll
// position untouched, so there is no jump — and it plays nicely with the
// window virtualizer on /my-listings, which would otherwise reshuffle rows
// when the scroll position is reset to 0.
//
// Counting locks means stacked overlays (e.g. an add-to-collection sheet over
// the manage sheet) only restore the original styles once the last one closes,
// regardless of open/close order.

interface SavedScrollState {
  htmlOverflow: string
  bodyOverflow: string
  bodyTouchAction: string
  bodyOverscrollBehavior: string
  bodyPaddingRight: string
}

let lockCount = 0
let saved: SavedScrollState | null = null

function applyLock() {
  const html = document.documentElement
  const body = document.body
  const scrollbarWidth = window.innerWidth - html.clientWidth

  saved = {
    htmlOverflow: html.style.overflow,
    bodyOverflow: body.style.overflow,
    bodyTouchAction: body.style.touchAction,
    bodyOverscrollBehavior: body.style.overscrollBehavior,
    bodyPaddingRight: body.style.paddingRight,
  }

  html.style.overflow = "hidden"
  body.style.overflow = "hidden"
  body.style.touchAction = "none"
  body.style.overscrollBehavior = "none"
  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`
  }
}

function releaseLock() {
  if (!saved) return

  const html = document.documentElement
  const body = document.body

  html.style.overflow = saved.htmlOverflow
  body.style.overflow = saved.bodyOverflow
  body.style.touchAction = saved.bodyTouchAction
  body.style.overscrollBehavior = saved.bodyOverscrollBehavior
  body.style.paddingRight = saved.bodyPaddingRight
  saved = null
}

export function useBodyScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked || typeof window === "undefined") return

    lockCount += 1
    if (lockCount === 1) applyLock()

    return () => {
      lockCount -= 1
      if (lockCount === 0) releaseLock()
    }
  }, [locked])
}
